import React, {
  useState,
  useEffect,
  useCallback, 
  useReducer,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import FormRenderer from "./FormRenderer";
import "./FormRenderer.css";
import ReturnOrReassignModal from "../../components/ReturnOrReassignModal";
import SubmissionHistoryViewer from "../../Forms/BrokerForm/SubmissionHistoryViewer";
import api from "../../services/api";

// --- API Constants ---
const API_BASE_URL = api.defaults.baseURL;
const GET_FORM_URL = (formId) =>
  `${API_BASE_URL}/broker/assigned-forms/${formId}`;
const GET_SUBMISSION_HISTORY_URL = (formId) =>
  `${API_BASE_URL}/manager/submissions/${formId}/history`;
const GET_SUBMISSION_BY_ID_URL = (subId) =>
  `${API_BASE_URL}/manager/submissions/${subId}`;
const SUBMIT_FORM_URL = (formId) =>
  `${API_BASE_URL}/broker/assigned-forms/${formId}`;
const RETURN_FORM_URL = (formId) =>
  `${API_BASE_URL}/manager/assigned-forms/${formId}/return`;
const FINALIZE_FORM_URL = (formId) =>
  `${API_BASE_URL}/manager/assigned-forms/${formId}/finalize`;
const GET_LOT_DETAILS_URL = (lotNumber) =>
  `${API_BASE_URL}/broker/home-plans/${lotNumber}`;

// --- NEW: Helper function to create the unique key for localStorage ---
const getDraftKey = (formId) => `draft-form-${formId}`;

// --- Price helpers (robust) ---
const getItemPriceFromTemplate = (template, fieldName) => {
  if (!template || !Array.isArray(template.sections)) return 0;
  const items = template.sections.flatMap(s => s.items || []);
  const item = items.find(i => i.fieldName === fieldName);
  const n = Number(item?.price);
  return Number.isFinite(n) ? n : 0;
};

const _normalizeSnapshot = (snap = {}) => ({
  baseHome: Number(snap.baseHome ?? snap.basehome ?? snap.base_home ?? 0),
  lotPremium: Number(snap.lotPremium ?? snap.lotpremium ?? snap.lot_premium ?? 0),
  unfinishedBasement: Number(snap.unfinishedBasement ?? snap.UnfinishedBasement ?? snap.unfinished_basement ?? 0),
  finishedBasement: Number(snap.finishedBasement ?? snap.finished_basement ?? snap.finished_basement ?? 0),
});

const applyPriceMapToTemplate = (template, priceMap = {}) => {
  if (!template) return template;
  const t = JSON.parse(JSON.stringify(template));
  if (!Array.isArray(t.sections)) return t;
  const snap = _normalizeSnapshot(priceMap);
  const set = (fieldName, val) => {
    if (val == null) return;
    t.sections.forEach(sec => {
      (sec.items || []).forEach(it => {
        if (it.fieldName === fieldName) it.price = val;
      });
    });
  };
  set('baseHome', snap.baseHome);
  set('lotPremium', snap.lotPremium);
  set('UnfinishedBasement', snap.unfinishedBasement);
  set('finishedBasement', snap.finishedBasement);
  return t;
};

const getCurrentPriceSnapshot = (template) => {
  if (!template) return { baseHome: 0, lotPremium: 0, unfinishedBasement: 0, finishedBasement: 0 };
  return {
    baseHome: getItemPriceFromTemplate(template, 'baseHome'),
    lotPremium: getItemPriceFromTemplate(template, 'lotPremium'),
    unfinishedBasement: getItemPriceFromTemplate(template, 'UnfinishedBasement'),
    finishedBasement: getItemPriceFromTemplate(template, 'finishedBasement'),
  };
};

// Try to derive snapshot from an existing formData when submission didn't include a priceSnapshot
const deriveSnapshotFromFormData = (formData = {}) => {
  try {
    const s = formData.selections || {};
    const valueOrZero = (keyCandidates) => {
      for (const k of keyCandidates) {
        const sel = s[k];
        if (sel) {
          // prefer manualPrice, then maybe price (some shapes), then 0
          const mp = Number(sel.manualPrice ?? sel.price ?? 0);
          if (!Number.isNaN(mp)) return mp;
        }
      }
      return 0;
    };
    return {
      baseHome: valueOrZero(['baseHome', 'BaseHome']),
      lotPremium: valueOrZero(['lotPremium', 'lot_premium', 'lotPremium']),
      unfinishedBasement: valueOrZero(['UnfinishedBasement', 'unfinishedBasement']),
      finishedBasement: valueOrZero(['finishedBasement', 'finishedBasement']),
    };
  } catch (e) {
    return { baseHome: 0, lotPremium: 0, unfinishedBasement: 0, finishedBasement: 0 };
  }
};

const initializeFormData = (template) => {
  if (!template) return null;
  const initialData = {
    header: {},
    selections: {},
    billing: {},
    signatures: {},
    comments: { main: "" },
  };

  template.headerInputFields.forEach((field) => {
    initialData.header[field.fieldName] = field.defaultValue || "";
  });

  template.sections.forEach((section) => {
    section.items.forEach((item) => {
      const isSqft = item.unit === "per_sqft" || item.unit === "sf";
      const quantity = isSqft
        ? parseInt(item.comments, 10) || 1
        : parseInt(item.comments, 10) || 1;
      const comment = isSqft ? "" : item.comments || "";
      initialData.selections[item.fieldName] = {
        selected: item.fieldType === "display_static",
        quantity: quantity,
        manualPrice: item.price || 0,
        comment: comment,
      };
    });
  });

  if (
    template.DigitalSignatureSection &&
    Array.isArray(template.DigitalSignatureSection)
  ) {
    template.DigitalSignatureSection.forEach((section) => {
      section.items.forEach((field) => {
        initialData.signatures[field.fieldName] = "";
      });
    });
  }

  initialData.billing.designUpgradesComment = "";
  initialData.billing.concessionsComment = "";
  template.billingSection.items.forEach((item) => {
    if (item.fieldType === "manual_input_currency")
      initialData.billing[item.fieldName] = item.defaultValue || 0;
  });

  return initialData;
};
const calculateAllTotals = (currentFormData, template) => {
  if (!template?.sections || !currentFormData?.selections) {
    return {};
  }
  const totals = {};
  const allItems = template.sections.flatMap((s) => s.items);
  const baseHomeItem = allItems.find((i) => i.fieldName === "baseHome");
  const unfinishedBasementItem = allItems.find(
    (i) => i.fieldName === "UnfinishedBasement"
  );
  const lotPremiumItem = allItems.find((i) => i.fieldName === "lotPremium");
  const creditsItem = allItems.find((i) => i.fieldName === "credits");
  totals.baseHomeTotal =
    (parseFloat(baseHomeItem?.price) || 0) +
    (parseFloat(unfinishedBasementItem?.price) || 0);
  let structuralUpgrades = parseFloat(lotPremiumItem?.price) || 0;
  allItems.forEach((item) => {
    const isBasePriceItem =
      item.fieldName === "baseHome" ||
      item.fieldName === "UnfinishedBasement" ||
      item.fieldName === "lotPremium";
    if (isBasePriceItem) {
      return;
    }
    const selection = currentFormData.selections[item.fieldName];
    if (selection && selection.selected) {
      const quantity = Number(selection.quantity) || 1;

      let itemValue = 0;
      if (item.fieldType === "manual_entry") {
        itemValue = parseFloat(selection.manualPrice) || 0;
      } else if (item.fieldType === "upgrade_item") {
        itemValue = (parseFloat(item.price) || 0) * quantity;
      }
      if (item.fieldName !== "credits") {
        structuralUpgrades += itemValue;
      }
    }
  });
  totals.structuralUpgradesTotal = structuralUpgrades;
  let creditsValue = 0;
  const creditSelection = currentFormData.selections[creditsItem?.fieldName];
  if (creditsItem && creditSelection && creditSelection.selected) {
    creditsValue = parseFloat(creditSelection.manualPrice) || 0;
  }

  totals.concessions = parseFloat(currentFormData.billing?.concessions) || 0;
  totals.TotalConcessions = totals.concessions + Math.abs(creditsValue);

  totals.designUpgradesTotal =
    parseFloat(currentFormData.billing?.designUpgradesTotal) || 0;
  totals.totalBaseWithUpgrades =
    totals.baseHomeTotal +
    totals.structuralUpgradesTotal +
    totals.designUpgradesTotal;
  totals.finalPrice = totals.totalBaseWithUpgrades - totals.TotalConcessions;
  const baseDeposit = totals.baseHomeTotal * 0.05;
  const upgradeDepositComponents =
    (parseFloat(lotPremiumItem?.price) || 0) + // Lot Premium
    (totals.structuralUpgradesTotal - (parseFloat(lotPremiumItem?.price) || 0)) + 
    totals.designUpgradesTotal; 
  const totalUpgradeDeposit = upgradeDepositComponents * 0.15;

  totals.deposit = baseDeposit + totalUpgradeDeposit;
  const concessionReduction = (totals.TotalConcessions || 0) * 0.05;
  totals.deposit = (totals.deposit || 0) - concessionReduction;
  totals.balanceAtClosing = (totals.finalPrice || 0) - (totals.deposit || 0);

  return totals;
};
const formReducer = (state, action) => {
  switch (action.type) {
    case "UPDATE_FIELD": {
      const { group, fieldName, value } = action.payload;
      return {
        ...state,
        formData: {
          ...state.formData,
          [group]: { ...state.formData[group], [fieldName]: value },
        },
      };
    }
    case "INITIALIZE_FORM":
      return {
        ...state,
        formTemplate: action.payload.template,
        formData: action.payload.data,
      };
    default:
      return state;
  }
};

const BrokerFormPage = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    []
  );
  const userRole = user?.role || "guest";

  const [state, dispatch] = useReducer(formReducer, {
    formTemplate: null,
    formData: null,
  });
  const { formTemplate, formData } = state;
  const [formStatus, setFormStatus] = useState("pending");
  const [baseInvoiceId, setBaseInvoiceId] = useState(null);
  const [calculatedTotals, setCalculatedTotals] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [displayInvoiceId, setDisplayInvoiceId] = useState(null);
  const [errors, setErrors] = useState({});
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    setIsLoading(true);
    setApiError(null);
    const token = localStorage.getItem("authToken");
    if (!token) {
      setApiError("Authentication Error. Please log in again.");
      setIsLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    let assignedFormResponse;

    const draftKey = getDraftKey(formId);
    const savedDraft = localStorage.getItem(draftKey);

    fetch(GET_FORM_URL(formId), { headers })
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then((assignedForm) => {
        assignedFormResponse = assignedForm;
        return fetch(GET_LOT_DETAILS_URL(assignedForm.lotNumber), { headers });
      })
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then((lotDetailsArray) => {
        const lotDetails = lotDetailsArray[0];
        if (!lotDetails)
          throw new Error(
            `No home plan details found for lot ${assignedFormResponse.lotNumber}.`
          );

        setFormStatus(assignedFormResponse.status);
        setBaseInvoiceId(assignedFormResponse.baseInvoiceId);
        const latestSubmission =
          assignedFormResponse.submissions &&
          assignedFormResponse.submissions[0];
        const currentInvoiceIdToShow = latestSubmission
          ? latestSubmission.submissionInvoiceId
          : assignedFormResponse.baseInvoiceId;
        setDisplayInvoiceId(currentInvoiceIdToShow);

        if (latestSubmission) {
          setSelectedSubmissionId(latestSubmission._id);
        }

        const dynamicTemplate = JSON.parse(
          JSON.stringify(assignedFormResponse.templateSnapshot)
        );

        // --- robust draft parse ---
        let finalFormData;
        let parsedDraft = null;
        if (savedDraft) {
          try {
            parsedDraft = JSON.parse(savedDraft);
          } catch (e) {
            console.warn("Could not parse saved draft", e);
            parsedDraft = null;
          }
        }

        if (parsedDraft) {
          finalFormData = parsedDraft.formData ? parsedDraft.formData : parsedDraft;
          setFeedbackMessage({ type: "info", text: "Loaded your saved draft." });
          setTimeout(() => setFeedbackMessage({ type: "", text: "" }), 3500);
        } else if (latestSubmission) {
          finalFormData = latestSubmission.formData;
        } else {
          finalFormData = initializeFormData(dynamicTemplate);
        }

        const lotPriceMap = {
          baseHome: lotDetails.homePrice ?? 0,
          lotPremium: lotDetails.lotPrice ?? 0,
          unfinishedBasement: lotDetails.unfinishedBasement ?? 0,
          finishedBasement: lotDetails.finishedBasement ?? 0,
        };

        // Decide priceToUse depending on status:
        let priceToUse = _normalizeSnapshot(lotPriceMap);
        const status = (assignedFormResponse.status || "").toString().toLowerCase();

        if (status === "submitted" || status === "finalized") {
          // For submitted/finalized: prefer submission snapshot -> derive from submission -> fallback to lotPriceMap
          if (latestSubmission?.formData?.priceSnapshot) {
            priceToUse = _normalizeSnapshot(latestSubmission.formData.priceSnapshot);
          } else if (latestSubmission?.formData) {
            priceToUse = _normalizeSnapshot(deriveSnapshotFromFormData(latestSubmission.formData));
          } else if (parsedDraft?.priceSnapshot) {
            // (very rare) if no submission but parsedDraft exists, use draft snapshot, but we don't prefer it over a real submission
            priceToUse = _normalizeSnapshot(parsedDraft.priceSnapshot);
          }
        } else {
          // pending / reassigned: allow draft override, else submission snapshot, else lot values
          if (parsedDraft && parsedDraft.priceSnapshot) {
            priceToUse = _normalizeSnapshot(parsedDraft.priceSnapshot);
          } else if (latestSubmission?.formData?.priceSnapshot) {
            priceToUse = _normalizeSnapshot(latestSubmission.formData.priceSnapshot);
          }
        }

        const dynamicTemplateWithPrices = applyPriceMapToTemplate(dynamicTemplate, priceToUse);

        finalFormData.header = {
          ...finalFormData.header,
          lotNumber: assignedFormResponse.lotNumber,
          housePlanName: lotDetails.homePlanName,
          elevation: lotDetails.elevation,
          housePlanImageUrl: lotDetails.imageUrl,
          housePlanId: assignedFormResponse.baseInvoiceId,
          brokerName: user.name || "",
          brokerPhone: user.phoneNumber || "",
          brokerEmail: user.email || "",
        };
        const todaysDateField = (dynamicTemplateWithPrices.headerInputFields || []).find(
          (f) => f.fieldType === "display_date"
        );
        if (todaysDateField) {
          finalFormData.header[todaysDateField.fieldName] =
            new Date().toISOString();
        }

        dispatch({
          type: "INITIALIZE_FORM",
          payload: { template: dynamicTemplateWithPrices, data: finalFormData },
        });

        if (userRole === "manager") {
          return fetch(GET_SUBMISSION_HISTORY_URL(formId), { headers });
        } else {
          return Promise.resolve(null);
        }
      })
      .then((res) => {
        if (res && res.ok) return res.json();
        return null;
      })
      .then((historyData) => {
        if (historyData) {
          setSubmissionHistory(historyData);
        }
      })
      .catch((err) => setApiError(`Could not load form data. (${err.message})`))
      .finally(() => setIsLoading(false));
  }, [formId, user, userRole]);

  useEffect(() => {
    if (formData && formTemplate) {
      setCalculatedTotals(calculateAllTotals(formData, formTemplate));
    }
  }, [formData, formTemplate]);

  const {
    "1st-HomeownerFirstName": fn1,
    "1st-HomeownerLastName": ln1,
    "2nd-HomeownerFirstName": fn2,
    "2nd-HomeownerLastName": ln2,
  } = formData?.header || {};

  useEffect(() => {
    if (!formData?.signatures) return;
    const fullName1 = `${fn1 || ""} ${ln1 || ""}`.trim();
    const fullName2 = `${fn2 || ""} ${ln2 || ""}`.trim();

    if (formData.signatures["1st-homeownerName"] !== fullName1) {
      dispatch({
        type: "UPDATE_FIELD",
        payload: {
          group: "signatures",
          fieldName: "1st-homeownerName",
          value: fullName1,
        },
      });
    }
    if (formData.signatures["2nd-homeownerName"] !== fullName2) {
      dispatch({
        type: "UPDATE_FIELD",
        payload: {
          group: "signatures",
          fieldName: "2nd-homeownerName",
          value: fullName2,
        },
      });
    }
  }, [fn1, ln1, fn2, ln2, formData?.signatures, dispatch]);

  const handleFormChange = useCallback(
    (group, fieldName, value) => {
      dispatch({ type: "UPDATE_FIELD", payload: { group, fieldName, value } });
      if (errors[fieldName]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    },
    [errors]
  );

  const validateForm = () => {
    const newErrors = {};
    if (!formData || !formTemplate) return true;

    const { header, signatures } = formData;

    formTemplate.headerInputFields.forEach((field) => {
      if (field.isRequired && !header[field.fieldName]) {
        newErrors[field.fieldName] = `${field.label} is required.`;
      }
    });

    const phoneField = formTemplate.headerInputFields.find(
      (f) => f.fieldName === "homeownerPhone"
    );
    if (
      phoneField?.isRequired &&
      (header.homeownerPhone || "").replace(/\D/g, "").length !== 10
    ) {
      newErrors.homeownerPhone = "Phone number must contain exactly 10 digits.";
    }

    if (
      formTemplate.DigitalSignatureSection &&
      Array.isArray(formTemplate.DigitalSignatureSection)
    ) {
      formTemplate.DigitalSignatureSection.forEach((section) => {
        section.items.forEach((field) => {
          const isSecondHomeownerField =
            field.fieldName.includes("2nd-homeowner");
          const secondHomeownerExists =
            header["2nd-HomeownerFirstName"] || header["2nd-HomeownerLastName"];
          let isFieldRequired = field.isRequired;
          if (isSecondHomeownerField && !secondHomeownerExists) {
            isFieldRequired = false;
          }
          if (isFieldRequired && !signatures[field.fieldName]) {
            newErrors[field.fieldName] = `${field.label} is required.`;
          }
        });
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSelectHistoryVersion = async (submissionId) => {
    if (submissionId === selectedSubmissionId) return;

    setIsLoading(true);
    setApiError(null);
    setSelectedSubmissionId(submissionId);
    const token = localStorage.getItem("authToken");

    try {
      const response = await fetch(GET_SUBMISSION_BY_ID_URL(submissionId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const submissionData = await response.json();
      if (!response.ok)
        throw new Error(submissionData.message || "Could not load submission.");

      const templForHistory = applyPriceMapToTemplate(
        formTemplate || {},
        submissionData.formData?.priceSnapshot || deriveSnapshotFromFormData(submissionData.formData || {})
      );

      dispatch({
        type: "INITIALIZE_FORM",
        payload: { template: templForHistory, data: submissionData.formData },
      });

      setDisplayInvoiceId(submissionData.submissionInvoiceId);
    } catch (err) {
      setApiError(`Could not load historical submission: ${err.message}`);
      setSelectedSubmissionId(submissionHistory[0]?._id);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveForLater = () => {
    setIsSaving(true);
    setApiError(null);
    try {
      const draftKey = getDraftKey(formId);
      const draftToSave = { 
        ...formData, 
        priceSnapshot: getCurrentPriceSnapshot(formTemplate || {}) 
      };
      localStorage.setItem(draftKey, JSON.stringify(draftToSave));
      
      setFeedbackMessage({ type: 'success', text: 'Progress saved locally!' });
      setTimeout(() => setFeedbackMessage({ type: '', text: '' }), 3500);

    } catch (err) {
      console.error("Failed to save to localStorage", err);
      setApiError("Could not save form progress locally. Your browser might be in private mode or storage is full.");
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) {
      alert("Please correct the errors before submitting the form.");
      const firstErrorKey =
        Object.keys(errors)[0] || Object.keys(validateForm())[0];
      const errorElement = document.getElementById(firstErrorKey);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsSaving(true);
    setApiError(null);
    const token = localStorage.getItem("authToken");
    const priceSnapshot = getCurrentPriceSnapshot(formTemplate || {});

    const submissionPayload = {
      formData: {
        ...formData,
        priceSnapshot,
        grandTotal: calculatedTotals.finalPrice || 0,
      },
    };

    try {
      const response = await fetch(SUBMIT_FORM_URL(formId), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionPayload),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Submission failed.");

      const draftKey = getDraftKey(formId);
      localStorage.removeItem(draftKey);

      alert("Form submitted successfully!");
      navigate(
        userRole === "manager"
          ? "/manager/ManagerDashboard"
          : "/broker/dashboard"
      );
    } catch (err) {
      setApiError(`Could not submit form: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };
  const handleFinalize = async () => {
    if (!window.confirm("Are you sure you want to finalize this form?")) return;
    setIsSaving(true);
    setApiError(null);
    const token = localStorage.getItem("authToken");
    try {
      const response = await fetch(FINALIZE_FORM_URL(formId), {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message);
      alert(result.message);
      window.location.reload();
    } catch (err) {
      setApiError(`Action failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmReturn = async (newBrokerUserId) => {
    setIsSaving(true);
    setApiError(null);
    const token = localStorage.getItem("authToken");
    const requestOptions = {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
    if (newBrokerUserId) {
      requestOptions.body = JSON.stringify({ newBrokerUserId });
    }
    try {
      const response = await fetch(RETURN_FORM_URL(formId), requestOptions);
      const result = await response.json();
      if (!response.ok) throw new Error(result.message);
      alert(result.message);
      setIsReturnModalOpen(false);
      window.location.reload();
    } catch (err) {
      setApiError(`Action failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };
  const handleBackToDashboard = () =>
    navigate(
      userRole === "manager" ? "/manager/ManagerDashboard" : "/broker/dashboard"
    );

  if (isLoading && !formTemplate)
    return (
      <div className="p-5 text-center">
        <h3>Loading Form...</h3>
      </div>
    );
  if (apiError && !formTemplate)
    return <div className="alert alert-danger m-3">{apiError}</div>;
  if (!formTemplate || !formData)
    return (
      <div className="p-5 text-center">
        <h3>Error: Form could not be initialized.</h3>
      </div>
    );

  const hasHistory = submissionHistory && submissionHistory.length > 0;
  const isViewingHistory =
    hasHistory && selectedSubmissionId !== submissionHistory[0]?._id;

  return (
    <div className="form-renderer-container">
      {apiError && <div className="alert alert-danger mb-3">{apiError}</div>}
      {/* --- NEW: Display feedback messages --- */}
      {feedbackMessage.text && <div className={`form-feedback-alert alert-${feedbackMessage.type}`}>{feedbackMessage.text}</div>}


      {userRole === "manager" && (
        <SubmissionHistoryViewer
          history={submissionHistory}
          currentId={selectedSubmissionId}
          onSelect={handleSelectHistoryVersion}
          isLoading={isLoading}
        />
      )}

      <FormRenderer
        formTemplate={formTemplate}
        formData={formData}
        onFormChange={handleFormChange}
        onSubmit={handleSubmit}
        onFinalize={handleFinalize}
        onSaveForLater={handleSaveForLater} // This now points to the localStorage version
        calculatedTotals={calculatedTotals}
        errors={errors}
        isSaving={isSaving}
        formStatus={formStatus}
        displayInvoiceId={displayInvoiceId}
        userRole={userRole}
        onBackToDashboard={handleBackToDashboard}
        baseInvoiceId={baseInvoiceId}
        onReturnToBroker={() => setIsReturnModalOpen(true)}
        isReadOnlyOverride={isViewingHistory}
        isActionable={!isViewingHistory}
      />
      <ReturnOrReassignModal
        show={isReturnModalOpen}
        onHide={() => setIsReturnModalOpen(false)}
        onConfirm={handleConfirmReturn}
        isSaving={isSaving}
      />
    </div>
  );
};
export default BrokerFormPage;
